import { FC, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { observer } from 'mobx-react-lite';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { Button } from 'components/Core/Button';
import { Switch } from 'components/Core/Switch/Switch';
import CronDataSelect from 'components/Inputs/CronDataSelect/CronDataSelect';
import { IScriptBasicForm, ScriptBasicFormTransFields } from './types';
import { scriptBasicFormTranslations } from './translations';
import styles from './scriptBasicForm.module.scss';

const ScriptBasicForm: FC = () => {
  const {
    scriptsStore: { changeScriptMainData, scriptData },
    alertStore: { showAlert },
  } = useStore();

  const { t } = useMyTranslations<ScriptBasicFormTransFields>([scriptBasicFormTranslations.name]);

  const { mutate, isLoading } = useMutation(changeScriptMainData, {
    mutationKey: 'changeScriptMainData',
    onSuccess: () => {
      showAlert(t('saveSuccess'), 'success');
    },
  });

  const useFormMethods = useForm<IScriptBasicForm>({
    defaultValues: {
      cronExpression: scriptData?.cronExpression || '* * * * *',
      status: scriptData?.status,
    },
  });

  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { isSubmitting },
  } = useFormMethods;

  const onSubmitHandler = useCallback(
    (inputData: IScriptBasicForm) => {
      if (!scriptData) return;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { messageTranslations, ...data } = scriptData;
      mutate({ ...data, ...inputData });
    },
    [scriptData, mutate]
  );

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <FormProvider {...(useFormMethods as any)}>
      <div className={styles.content}>
        <form className={styles.form}>
          <section className={styles.leftSection}>
            <div className={styles.formSection}>
              <CronDataSelect name='cronExpression' label={t('cronExpression')} />
              <div className={styles.checkboxWrap}>
                <h6 className={styles.title}>{t('status')}</h6>
                <Switch
                  isActive={watch('status') === 'Active'}
                  onChange={() => setValue('status', getValues('status') === 'Active' ? 'Inactive' : 'Active')}
                />
              </div>
            </div>
            <div>
              <Button
                title={t('save')}
                onClick={handleSubmit(onSubmitHandler)}
                isDisabled={isSubmitting || isLoading}
              />
            </div>
          </section>
        </form>
      </div>
    </FormProvider>
  );
};

export default observer(ScriptBasicForm);
